class BackendAPI {
  constructor() {
    this.accessToken = window.customer?.accessToken || "";
    this.apiUrl = window.BACKEND_BASE_URL;

    this.headers = {
      Authorization: `Bearer ${this.accessToken}`,
      Accept: "application/json",
    };
  }

  /**
   * Get partner information for a given site
   * @param {string} site - The PWS site identifier
   * @returns {Promise<{
   *   displayName: string,
   *   email: string,
   *   country: string,
   *   advocateId: string,
   *   siteName: string,
   *   tags: string[] | null
   * } | null>} Partner information or null if not found
   */
  async getPartnerBySite(site, checkCache = true) {
    const cacheKey = `CACHE_PARTNER_${site}`;

    if (checkCache) {
      const cachedPartner = sessionStorage.getItem(cacheKey);
      if (cachedPartner) {
        return JSON.parse(cachedPartner);
      }
    }

    return fetch(`${this.apiUrl}/partners?site=${site}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          return data;
        } else {
          sessionStorage.setItem(cacheKey, null);
          return null;
        }
      })
      .catch((_) => {
        sessionStorage.setItem(cacheKey, null);
        return null;
      });
  }

  /**
   * Get partner information for a given advocateId
   * @param {string | number} advocateId - The advocateId
   * @returns {Promise<{
   *   displayName: string,
   *   email: string,
   *   country: string,
   *   advocateId: string,
   *   siteName: string,
   *   tags: string[] | null
   * } | null>} Partner information or null if not found
   */
  async getPartnerByAdvocateId(advocateId) {
    const cacheKey = `CACHE_PARTNER_${advocateId}`;

    const cachedPartner = sessionStorage.getItem(cacheKey);
    if (cachedPartner) {
      return JSON.parse(cachedPartner);
    }

    return fetch(`${this.apiUrl}/partners?advocateId=${advocateId}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          return data;
        } else {
          sessionStorage.setItem(cacheKey, null);
          return null;
        }
      })
      .catch((_) => {
        sessionStorage.setItem(cacheKey, null);
        return null;
      });
  }

  async setName(first_name, last_name) {
    const url = `${this.apiUrl}/partners/name`;
    return await fetch(url, {
      method: "PUT",
      body: JSON.stringify({ first_name, last_name }),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async setEmail(email) {
    const url = `${this.apiUrl}/partners/email`;
    return await fetch(url, {
      method: "PUT",
      body: JSON.stringify({ email }),
      headers: this.headers,
    }).then(async (res) => {
      return res.json();
    });
  }

  async setPhone(phone) {
    const url = `${this.apiUrl}/partners/phone`;
    return await fetch(url, {
      method: "PUT",
      body: JSON.stringify({ phone }),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async setPassword(password, confirm_password) {
    const url = `${this.apiUrl}/partners/password`;
    return await fetch(url, {
      method: "PUT",
      body: JSON.stringify({ password, confirm_password }),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async getImageUploadURL(file) {
    const url = `${this.apiUrl}/partners/image-upload-url?size=${file.size}&type=${file.type}`;

    return await fetch(url, {
      method: "GET",
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async setImageAsProfile(image_url) {
    const url = `${this.apiUrl}/partners/set-profile-image`;
    return await fetch(url, {
      method: "PUT",
      body: JSON.stringify({ image_url }),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async setMetafield(payload) {
    const url = `${this.apiUrl}/partners/metafield`;
    return await fetch(url, {
      method: "PUT",
      body: JSON.stringify(payload),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async getProductSearch(searchTerm) {
    return await fetch(
      `/search/suggest?q=${searchTerm}&resources[type]=product&resources[limit]=10&section_id=dashboard-predictive-search`
    )
      .then((res) => {
        if (res.ok) {
          return res.text();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async getFeaturedProducts(customerId) {
    const url = `${this.apiUrl}/partners/featured-products?customerId=${customerId}`;
    return await fetch(url)
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async addFeaturedProduct(variantId, productHandle, feedback) {
    const url = `${this.apiUrl}/partners/featured-products`;
    return await fetch(url, {
      method: "POST",
      body: JSON.stringify({ variantId, productHandle, feedback }),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async reorderFeaturedProduct(newOrder) {
    const url = `${this.apiUrl}/partners/featured-products/reorder`;
    return await fetch(url, {
      method: "POST",
      body: JSON.stringify({ newOrder }),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async updateFeaturedProduct(variantId, feedback) {
    const url = `${this.apiUrl}/partners/featured-products/${variantId}`;
    return await fetch(url, {
      method: "PATCH",
      body: JSON.stringify({ feedback }),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async deleteFeaturedProduct(variantId) {
    const url = `${this.apiUrl}/partners/featured-products/${variantId}`;
    return await fetch(url, {
      method: "DELETE",
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async putPartnerSiteName(payload) {
    const url = `${this.apiUrl}/partners/site`;
    return await fetch(url, {
      method: "PUT",
      body: JSON.stringify(payload),
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async getExigoLoginURL() {
    const url = `${this.apiUrl}/sso/login`;
    return fetch(url, {
      method: "POST",
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }
}

// Initialize the backend API and make it available globally
window.backend = new BackendAPI();
